import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import {
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import { EmailDomainsTable } from "@/components/tables/email-domains";
import { MemberAreaDomainsTable } from "@/components/tables/member-area-domains";
import { Button } from "@/components/ui/button";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobile";
import type { dominios_domains$key } from "./__generated__/dominios_domains.graphql";
import type { dominios_emailDomains$key } from "./__generated__/dominios_emailDomains.graphql";
import type { dominiosCreateDomainMutation } from "./__generated__/dominiosCreateDomainMutation.graphql";
import type { dominiosCreateEmailDomainMutation } from "./__generated__/dominiosCreateEmailDomainMutation.graphql";
import type { dominiosQuery } from "./__generated__/dominiosQuery.graphql";

const defaultValues = {
	view: "area-de-membros",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/dominios",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["area-de-membros", "email"]).default("area-de-membros"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function MemberAreaDomains({
	domainsRef,
}: {
	domainsRef: dominios_domains$key;
}) {
	const [data, refetch] = useRefetchableFragment(
		graphql`
			fragment dominios_domains on Club
			@refetchable(queryName: "dominiosDomainsRefetchQuery") {
				domains {
					id
					domain
					status
					enabled
					isDefault
					verifiedAt
					dnsInfos {
						name
						type
						value
					}
				}
			}
		`,
		domainsRef,
	);

	return (
		<div className="mt-6">
			<MemberAreaDomainsTable
				data={data.domains.filter(Boolean)}
				onDeleted={() => refetch({}, { fetchPolicy: "network-only" })}
			/>
		</div>
	);
}

function EmailDomains({
	emailDomainsRef,
}: {
	emailDomainsRef: dominios_emailDomains$key;
}) {
	const [data, refetch] = useRefetchableFragment(
		graphql`
			fragment dominios_emailDomains on Club
			@refetchable(queryName: "dominiosEmailDomainsRefetchQuery") {
				emailDomains {
					id
					domain
					status
					enabled
					createdAt
					verifiedAt
				}
			}
		`,
		emailDomainsRef,
	);

	return (
		<div className="mt-6">
			<EmailDomainsTable
				data={data.emailDomains.filter(Boolean)}
				onDeleted={() => refetch({}, { fetchPolicy: "network-only" })}
			/>
		</div>
	);
}

const domainSchema = z.object({
	domain: z
		.string()
		.min(1, "O domínio é obrigatório")
		.regex(
			/^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
			"Informe um domínio válido (ex: meu.dominio.com)",
		),
});

type DnsRecord = { type: string; name: string | null; value: string };

function DnsRecordsInfo({
	domain,
	records,
}: {
	domain: string;
	records: DnsRecord[];
}) {
	return (
		<div className="space-y-4">
			<div className="rounded-md border bg-emerald-50 p-3">
				<p className="text-sm font-medium text-emerald-800">
					Domínio adicionado com sucesso!
				</p>
				<p className="text-sm text-emerald-700 mt-1">
					Configure os registros DNS abaixo no seu provedor para verificar{" "}
					<span className="font-medium">{domain}</span>.
				</p>
			</div>

			<div className="rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Tipo</TableHead>
							<TableHead>Nome</TableHead>
							<TableHead>Valor</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{records.map((record, i) => (
							<TableRow key={i}>
								<TableCell className="font-mono text-sm">
									{record.type}
								</TableCell>
								<TableCell className="font-mono text-sm">
									{record.name ?? "-"}
								</TableCell>
								<TableCell className="font-mono text-sm max-w-[200px]">
									<div className="flex items-center gap-2">
										<span title={record.value} className="truncate">
											{record.value}
										</span>
										<Button
											type="button"
											variant="ghost"
											size="icon-sm"
											className="shrink-0"
											onClick={() => {
												navigator.clipboard.writeText(record.value);
												toast.success(
													"Valor copiado para a área de transferência",
												);
											}}
										>
											<Icon name="Copy" className="size-3.5" />
										</Button>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			<p className="text-sm text-muted-foreground">
				Após configurar o DNS, a propagação pode levar até 48 horas. Você pode
				verificar o status na tabela de domínios.
			</p>
		</div>
	);
}

function AddMemberAreaDomainDrawer({
	open,
	onOpenChange,
	clubSlug,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	clubSlug: string;
}) {
	const isMobile = useIsMobile();
	const [dnsResult, setDnsResult] = useState<{
		domain: string;
		records: DnsRecord[];
	} | null>(null);

	const [createDomain, isCreating] =
		useMutation<dominiosCreateDomainMutation>(graphql`
			mutation dominiosCreateDomainMutation(
				$club: String!
				$input: CreateDomainInput!
			) {
				createDomain(club: $club, createDomainInput: $input) {
					id
					domain
					dnsInfos {
						name
						type
						value
					}
					club {
						id
						...dominios_domains
					}
				}
			}
		`);

	const form = useForm({
		defaultValues: {
			domain: "",
		},
		validators: {
			onChange: domainSchema,
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createDomain({
					variables: {
						club: clubSlug,
						input: { domain: value.domain },
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						const dnsInfos = response.createDomain.dnsInfos;
						if (dnsInfos) {
							setDnsResult({
								domain: response.createDomain.domain,
								records: [
									{
										type: dnsInfos.type,
										name: dnsInfos.name,
										value: dnsInfos.value,
									},
								],
							});
						} else {
							toast.success("Domínio adicionado!");
							handleClose();
						}
						resolve();
					},
					onError: (error) => {
						toast.error(
							error.message || "Erro ao adicionar domínio. Tente novamente.",
						);
						reject(error);
					},
				});
			});
		},
	});

	function handleClose() {
		onOpenChange(false);
		setDnsResult(null);
		form.reset();
	}

	return (
		<Drawer
			open={open}
			onOpenChange={(isOpen) => {
				if (!isOpen) handleClose();
			}}
			direction={isMobile ? "bottom" : "right"}
		>
			<DrawerContent>
				{dnsResult ? (
					<>
						<DrawerHeader>
							<DrawerTitle>Instruções DNS</DrawerTitle>
							<DrawerDescription>
								Configure os registros abaixo no seu provedor de domínio.
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4">
							<DnsRecordsInfo
								domain={dnsResult.domain}
								records={dnsResult.records}
							/>
						</div>

						<DrawerFooter>
							<Button onClick={handleClose}>Fechar</Button>
						</DrawerFooter>
					</>
				) : (
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Adicionar domínio</DrawerTitle>
							<DrawerDescription>
								Adicione um domínio personalizado para a área de membros da sua
								comunidade. Após adicionar, você precisará configurar o DNS no
								seu provedor.
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<form.Field name="domain">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>Domínio</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="membros.meusite.com"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>
										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>
						</div>

						<DrawerFooter>
							<form.Subscribe selector={(state) => [state.isSubmitting]}>
								{([isSubmitting]) => (
									<>
										<Button type="submit" disabled={isSubmitting || isCreating}>
											{isSubmitting || isCreating
												? "Adicionando..."
												: "Adicionar"}
										</Button>
										<DrawerClose asChild>
											<Button variant="outline" type="button">
												Cancelar
											</Button>
										</DrawerClose>
									</>
								)}
							</form.Subscribe>
						</DrawerFooter>
					</form>
				)}
			</DrawerContent>
		</Drawer>
	);
}

function AddEmailDomainDrawer({
	open,
	onOpenChange,
	clubSlug,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	clubSlug: string;
}) {
	const isMobile = useIsMobile();
	const [dnsResult, setDnsResult] = useState<{
		domain: string;
		records: DnsRecord[];
	} | null>(null);

	const [createEmailDomain, isCreating] =
		useMutation<dominiosCreateEmailDomainMutation>(graphql`
			mutation dominiosCreateEmailDomainMutation(
				$club: String!
				$input: CreateEmailDomainInput!
			) {
				createEmailDomain(
					club: $club
					createEmailDomainInput: $input
				) {
					club {
						id
						...dominios_emailDomains
					}
					records {
						type
						name
						value
					}
				}
			}
		`);

	const form = useForm({
		defaultValues: {
			domain: "",
		},
		validators: {
			onChange: domainSchema,
		},
		onSubmit: async ({ value }) => {
			return new Promise<void>((resolve, reject) => {
				createEmailDomain({
					variables: {
						club: clubSlug,
						input: { domain: value.domain },
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						const records = response.createEmailDomain.records;
						if (records.length > 0) {
							setDnsResult({
								domain: value.domain,
								records: records.map((r) => ({
									type: r.type,
									name: r.name ?? null,
									value: r.value,
								})),
							});
						} else {
							toast.success("Domínio de email adicionado!");
							handleClose();
						}
						resolve();
					},
					onError: (error) => {
						toast.error(
							error.message ||
								"Erro ao adicionar domínio de email. Tente novamente.",
						);
						reject(error);
					},
				});
			});
		},
	});

	function handleClose() {
		onOpenChange(false);
		setDnsResult(null);
		form.reset();
	}

	return (
		<Drawer
			open={open}
			onOpenChange={(isOpen) => {
				if (!isOpen) handleClose();
			}}
			direction={isMobile ? "bottom" : "right"}
		>
			<DrawerContent>
				{dnsResult ? (
					<>
						<DrawerHeader>
							<DrawerTitle>Instruções DNS</DrawerTitle>
							<DrawerDescription>
								Configure os registros abaixo no seu provedor de domínio.
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4">
							<DnsRecordsInfo
								domain={dnsResult.domain}
								records={dnsResult.records}
							/>
						</div>

						<DrawerFooter>
							<Button onClick={handleClose}>Fechar</Button>
						</DrawerFooter>
					</>
				) : (
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<DrawerHeader>
							<DrawerTitle>Adicionar domínio de email</DrawerTitle>
							<DrawerDescription>
								Adicione um domínio personalizado para envio de emails da sua
								comunidade. Será necessário configurar registros DNS para
								verificação.
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4 space-y-4">
							<form.Field name="domain">
								{(field) => (
									<Field>
										<FieldLabel htmlFor={field.name}>Domínio</FieldLabel>
										<Input
											id={field.name}
											name={field.name}
											placeholder="meusite.com"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>
										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>
						</div>

						<DrawerFooter>
							<form.Subscribe selector={(state) => [state.isSubmitting]}>
								{([isSubmitting]) => (
									<>
										<Button type="submit" disabled={isSubmitting || isCreating}>
											{isSubmitting || isCreating
												? "Adicionando..."
												: "Adicionar"}
										</Button>
										<DrawerClose asChild>
											<Button variant="outline" type="button">
												Cancelar
											</Button>
										</DrawerClose>
									</>
								)}
							</form.Subscribe>
						</DrawerFooter>
					</form>
				)}
			</DrawerContent>
		</Drawer>
	);
}

function RouteComponent() {
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const [isMemberAreaDrawerOpen, setIsMemberAreaDrawerOpen] = useState(false);
	const [isEmailDrawerOpen, setIsEmailDrawerOpen] = useState(false);

	const data = useLazyLoadQuery<dominiosQuery>(
		graphql`
			query dominiosQuery($slug: String!) {
				club(slug: $slug) {
					id
					slug
					domains {
						isDefault
					}
					...dominios_domains
					...dominios_emailDomains
				}
			}
		`,
		{ slug: club.slug },
	);

	const hasCustomDomain = data.club.domains.some((d) => !d.isDefault);

	return (
		<div className="p-6">
			<Tabs
				defaultValue="area-de-membros"
				value={view}
				onValueChange={(val) =>
					navigate({
						search: (s) => ({ ...s, view: val as "area-de-membros" | "email" }),
					})
				}
			>
				<div className="flex items-center justify-between">
					<TabsList>
						<TabsTrigger value="area-de-membros" className="border-none">
							Área de Membros
						</TabsTrigger>
						<TabsTrigger value="email" className="border-none">
							Email
						</TabsTrigger>
					</TabsList>

					{view === "area-de-membros" ? (
						<Button
							size="sm"
							onClick={() => setIsMemberAreaDrawerOpen(true)}
							disabled={hasCustomDomain}
						>
							<Icon name="Plus" className="size-4" />
							Adicionar domínio
						</Button>
					) : (
						<Button size="sm" onClick={() => setIsEmailDrawerOpen(true)}>
							<Icon name="Plus" className="size-4" />
							Adicionar domínio
						</Button>
					)}
				</div>

				<TabsContent value="area-de-membros">
					<MemberAreaDomains domainsRef={data.club} />
				</TabsContent>
				<TabsContent value="email">
					<EmailDomains emailDomainsRef={data.club} />
				</TabsContent>
			</Tabs>

			<AddMemberAreaDomainDrawer
				open={isMemberAreaDrawerOpen}
				onOpenChange={setIsMemberAreaDrawerOpen}
				clubSlug={club.slug}
			/>
			<AddEmailDomainDrawer
				open={isEmailDrawerOpen}
				onOpenChange={setIsEmailDrawerOpen}
				clubSlug={club.slug}
			/>
		</div>
	);
}
